import {BaseMuixPageController} from "~/components/muix-data-grid/controllers/BaseMuixPageController.ts";
import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import type AuthStore from "../../../../authentication/AuthStore.tsx";
import type {BagStore} from "~/stores/BagStore.tsx";
import {action, makeObservable, observable, reaction, runInAction} from "mobx";
import {BaseMuixApi} from "~/components/muix-data-grid/BaseMuixApi.ts";
import {SCTableController} from "./SCTableController.tsx";
import {SCFilterController} from "./SCFilterController.ts";
import type {NestedRelatedDataModel} from "~/services/ApiHelpers.ts";
import type {
    MReviewSpencConcentrationCategory,
    MReviewSpencConcentrationSupplier
} from "~/services/classes/MaterializedClasses.ts";
import type {AxiosResponse} from "axios";
import axios from "axios";
import type {MuiXGroupedResponse} from "~/components/muix-data-grid/MuixApiTypes.ts";
import type {BarDataPoint} from "~/components/visualization/bar-chart/BarChart2.tsx";
import type ProfileStore from "~/stores/ProfileStore.tsx";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "~/constants.ts";
import {toCurrency} from "~/components/currency-component/CurrencyClasses.ts";

export class SCPageController extends BaseMuixPageController {
    readonly tableController: SCTableController;
    readonly filterController: SCFilterController;
    readonly reviewApi: BaseMuixApi;
    readonly muixUrl = '/m_review/spend_concentration/muix';
    relatedDataFieldsSpec: NestedRelatedDataModel = {
        cat: Array.from(Array(8).keys()).map(i => 'l' + String(i + 1)),
        part: [],
        supplier: [],
        business_unit: ['bu_name'],
    }
    // Spend concentration value: fractions (0..1) of total spend held by the top 20 suppliers vs. the rest
    top20SupplierSpend?: number;
    otherThanTop20SupplierSpend?: number;
    topOtherLoading = false;
    // Category chart
    categoryChartLoading = false;
    categoryChartData: BarDataPoint[] = [];
    // Supplier chart variables
    supplierPage = 0;
    supplierPerPage = 25;
    supplierChartLoading = false;
    supplierChartCount = 0;
    supplierChartData: MReviewSpencConcentrationSupplier[] = [];
    // Aborts the previous in-flight request of each kind when a newer one is fired (e.g. rapid filter/search changes)
    private categoryChartAbortController?: AbortController;
    private supplierChartAbortController?: AbortController;
    private top20SupplierAbortController?: AbortController;
    // Inputs to top20SupplierSpend/otherThanTop20SupplierSpend, filled in independently by
    // setCategoryChartData (denominator) and setTop20SupplierSpend (numerator)
    private totalCategorySpend?: number;
    private top20RawSpend?: number;

    constructor(
        private api: MithraMaterializedApi,
        // Note that declaring this as private would result in this error
        //      Types have separate declarations of a private property
        protected auth: AuthStore,
        protected bagStore: BagStore,
        protected profileStore: ProfileStore,
    ) {
        super(auth);
        makeObservable(this, {
            // Spend concentration value
            top20SupplierSpend: observable,
            otherThanTop20SupplierSpend: observable,
            topOtherLoading: observable,
            startTopOtherRefresh: action,
            // Category chart
            categoryChartLoading: observable,
            categoryChartData: observable,
            setCategoryChartData: action,
            setTop20SupplierSpend: action,
            // Supplier chart variables
            supplierPage: observable,
            supplierPerPage: observable,
            supplierChartLoading: observable,
            supplierChartCount: observable,
            supplierChartData: observable,
            setSupplierChartData: action,
            setSupplierPage: action,
            setSupplierPerPage: action,
        });
        this.tableController = new SCTableController(auth, bagStore);
        this.filterController = new SCFilterController(this, bagStore);
        this.reviewApi = new BaseMuixApi(this.api, this.muixUrl, this.tableController);
        reaction(() => ([this.bagStore.categorizationColumns]), ([categorizationColumns]) => {
            this.relatedDataFieldsSpec['part'] = categorizationColumns.filter(c =>
                c.table_name === 'data_source_part' && c.in_use
            ).map(c => c.column_name);
            this.relatedDataFieldsSpec['supplier'] = categorizationColumns.filter(c =>
                c.table_name === 'data_source_supplier' && c.in_use
            ).map(c => c.column_name);
        });
        reaction(() => ([this.bagStore.bagId, this.filterController.apiGridFilterModel]), ([_b, _f]) => {
            this.startTopOtherRefresh();
            this.setCategoryChartData();
            this.setTop20SupplierSpend();
            this.setSupplierPage(0);
        });
    }

    setCategoryChartData() {
        this.categoryChartLoading = true;
        this.categoryChartData = [];
        this.categoryChartAbortController?.abort();
        const controller = new AbortController();
        this.categoryChartAbortController = controller;
        const query = BaseMuixApi.getMuiXQuery(this.bagStore.bagId, {
            sortModel: [{field: 'p_spend_agg', sort: "desc"}],
            filterModel: this.filterController.apiGridFilterModel,
            groupKeys: [],
            groupFields: ['cat__l' + String(this.filterController.selectedLevel + 1)],
            relatedData: this.relatedDataFieldsSpec,
            noPage: true,
        });
        (this.reviewApi.materializedApi.http.get(
            `${this.muixUrl}/grid/`, {params: query, signal: controller.signal}
        ) as Promise<AxiosResponse<Array<MReviewSpencConcentrationCategory>>>).then((resp) => {
            runInAction(() => {
                const currencySymbol = this.profileStore.currencySymbol;
                // The bars partition all spend matching the current filters, so their sum is the total
                // for the current filter/level - reused below as the top20/other split's denominator.
                const totalSpend = resp.data.reduce((sum, r) => sum + Number(r.p_spend_agg), 0);
                this.totalCategorySpend = totalSpend;
                this.categoryChartData = resp.data.map<BarDataPoint>(r => {
                    // p_spend_agg is serialized as a string by DRF's DecimalField (COERCE_DECIMAL_TO_STRING),
                    // and d3.max compares BarDataPoint.value lexicographically when it isn't a real number.
                    const spend = Number(r.p_spend_agg);
                    const sharePercentage = totalSpend > 0 ? spend / totalSpend : 0;
                    return {
                        category: r.group,
                        categoryLabel: r.group === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : r.group,
                        value: spend,
                        valueLabel: `${Math.round(sharePercentage * 100)}%  |  ${toCurrency(currencySymbol, spend)}  |  ${r.distinct_suppliers} suppliers`,
                    };
                });
                this.categoryChartLoading = false;
                this.recomputeTopOtherSupplierSpend();
            });
        }).catch(err => {
            if (axios.isCancel(err)) return; // Superseded by a newer request
            throw err;
        });
    }

    /**
     * Fetches the spend of the (fixed) top 20 highest-spending suppliers under the current filters, through
     * the same grid endpoint as everything else so category/search/table filters apply consistently.
     * Combined with the category chart's total (see setCategoryChartData) into top20SupplierSpend/otherThanTop20SupplierSpend.
     */
    setTop20SupplierSpend() {
        this.top20SupplierAbortController?.abort();
        const controller = new AbortController();
        this.top20SupplierAbortController = controller;
        const query = BaseMuixApi.getMuiXQuery(this.bagStore.bagId, {
            sortModel: [{field: 'p_spend_agg', sort: "desc"}],
            filterModel: this.filterController.apiGridFilterModel,
            groupKeys: [],
            groupFields: ['s_name'],
            relatedData: this.relatedDataFieldsSpec,
            page: 1,
            pageSize: 20,
        });
        (this.reviewApi.materializedApi.http.get(
            `${this.muixUrl}/grid/`, {params: query, signal: controller.signal}
        ) as Promise<AxiosResponse<MuiXGroupedResponse<MReviewSpencConcentrationSupplier>>>).then((resp) => {
            runInAction(() => {
                this.top20RawSpend = resp.data.rows.reduce((sum, r) => sum + Number(r.p_spend_agg), 0);
                this.recomputeTopOtherSupplierSpend();
            });
        }).catch(err => {
            if (axios.isCancel(err)) return; // Superseded by a newer request
            throw err;
        });
    }

    /**
     * Resets the top20/other inputs before a new pair of (category, top-20-supplier) requests is fired,
     * so a fetch resolving early can't pair a fresh value with a stale one from the previous filter.
     */
    startTopOtherRefresh() {
        this.topOtherLoading = true;
        this.totalCategorySpend = undefined;
        this.top20RawSpend = undefined;
        this.top20SupplierSpend = undefined;
        this.otherThanTop20SupplierSpend = undefined;
    }

    private recomputeTopOtherSupplierSpend() {
        if (!this.totalCategorySpend || this.top20RawSpend === undefined) {
            return; // Still waiting on the other of the two requests
        }
        this.top20SupplierSpend = this.top20RawSpend / this.totalCategorySpend;
        this.otherThanTop20SupplierSpend = 1 - this.top20SupplierSpend;
        this.topOtherLoading = false;
    }

    setSupplierChartData() {
        this.supplierChartLoading = true;
        this.supplierChartData = [];
        this.supplierChartAbortController?.abort();
        const controller = new AbortController();
        this.supplierChartAbortController = controller;
        const query = BaseMuixApi.getMuiXQuery(this.bagStore.bagId, {
            sortModel: [{field: 'p_spend_agg', sort: "desc"}],
            filterModel: this.filterController.apiGridFilterModel,
            groupKeys: [],
            groupFields: ['s_name'],
            relatedData: this.relatedDataFieldsSpec,
            page: this.supplierPage + 1,
            pageSize: this.supplierPerPage,
        });
        (this.reviewApi.materializedApi.http.get(
            `${this.muixUrl}/grid/`, {params: query, signal: controller.signal}
        ) as Promise<AxiosResponse<MuiXGroupedResponse<MReviewSpencConcentrationSupplier>>>).then((resp) => {
            runInAction(() => {
                this.supplierChartCount = resp.data.rowCount;
                this.supplierChartData = resp.data.rows;
                this.supplierChartLoading = false;
            });
        }).catch(err => {
            if (axios.isCancel(err)) return; // Superseded by a newer request
            throw err;
        });
    }

    setSupplierPage = (value: number) => {
        this.supplierPage = value;
        this.setSupplierChartData();
    }

    setSupplierPerPage = (value: number) => {
        this.supplierPerPage = value;
        this.setSupplierPage(0);
    }
}
